// src/router.js
import React, { lazy, useMemo } from 'react';
import { BrowserRouter, createBrowserRouter, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import LanguageSelector from './components/Language/LanguageSelector';
import TranslatedComponent from './components/Language/TranslatedComponent';
import Login from './hooks/Sessions/Login';
import Register from './hooks/Sessions/Register';
// import './app.scss';
import Profile from './hooks/Sessions/Profile';
// import EssenceList from './components/Essences/EssencesList';
// import EssenceTypes from './components/Essences/EssenceTypes';
// import Checkout from './pages/Checkout/Checkout';
import { setRates } from "./Store/reducers/currencyReducer";
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { getUserAddresses, saveAddress } from './hooks/Services/AuthContext';
import ConfirmEmail from './hooks/Sessions/SendEmail';
import FeaturedProducts from './components/FeaturedProducts/FeaturedProducts';
import Addresses from './hooks/Sessions/Addresses';
import CurrencySwitcher from './Store/CurrencySwitcher';
import ScrollToTop from './components/Scroll/Scroll';
import CheckoutPage from './pages/Orders/Checkout/CheckoutPage';
import ResetPassword from './hooks/Sessions/ResetPassword';
import Reviews from './components/Reviews/Reviews';
import Logo from './components/Logo/Logo';
import FeaturedDesc from './components/FeaturedProducts/FeaturedDesc';
import { cartReducer } from './Store/reducers/cartReducer';
import { useState, useEffect } from 'react'
import Home from "./pages/Home/Home"
import { ThemeModeProvider } from './ThemeModeContext';
import { useColorScheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import usePageTitle from './hooks/usePageTitle';



import { ThemeProvider } from '@mui/material/styles';
import getTheme from './theme';
import ShippingInfoPage from './pages/Orders/Shipping/ShipingInfoPage';
import PayUPaymentResultPage from './pages/PayUPaymentResultPage.jsx';
import OrderStatusPage from './pages/OrderStatusPage.jsx';


// Lazy-loaded components
// const Home = lazy(() => import('./pages/Home/Home'));
const Product = lazy(() => import('./pages/Product/Product'));
const Products = lazy(() => import('./pages/Products/Products'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const About = lazy(() => import('./pages/About/About'));
const Wishlists = lazy(() => import('./pages/Wishlist/Wishlist'));
const Policy = lazy(() => import('./pages/Policy/Policy'));
const Returns = lazy(() => import('./pages/Returns/Returns'));
const Regulations = lazy(() => import('./pages/Regulations/Regulations'));
const SendEmail = lazy(() => import('./hooks/Sessions/SendEmail'));


// Layout components
const Footer = lazy(() => import('./components/Footer/Footer'));
const NavBar = lazy(() => import('./components/NavBar/NavBar'));

// Tab title (translation id) per static route. Product and category pages set
// their own title from fetched data, so they are left out here.
const PAGE_TITLES = {
    '/about': 'about',
    '/contact': 'contact',
    '/wishlists/me': 'wishlist_title',
    '/payment/success': 'page_title_payment',
    '/login': 'login',
    '/register': 'register',
    '/profile': 'profile',
    '/profile/orders': 'profile_my_orders',
    '/checkout': 'checkout',
    '/addresses': 'profile_my_addresses',
    '/policy': 'policy',
    '/returns': 'returns',
    '/regulations': 'regulations',
    '/change-password': 'reset_password_heading',
    '/reset-password': 'reset_password_heading',
    '/reviews': 'reviews_heading',
    '/shipping': 'shipping',
};
const PAGE_MANAGED_TITLE = /^\/(products?|wishlists\/me)\//;
// Account, checkout and order pages: kept out of search results (also in robots.txt).
const NOINDEX_PATH = /^\/(checkout|login|register|profile|addresses|wishlists|payment|order-status|reset-password|change-password|user-confirmed|carts|orders|currencies)(\/|$)/;

const StaticPageTitle = ({ pathname }) => {
    const intl = useIntl();
    const path = pathname.replace(/\/+$/, '') || '/';
    let titleId = PAGE_TITLES[path];
    if (path.startsWith('/order-status/')) titleId = 'page_title_order_status';
    usePageTitle(titleId ? intl.formatMessage({ id: titleId }) : null, { path, noindex: NOINDEX_PATH.test(path) });
    return null;
};

const Layout = () => {
    const location = useLocation();
    const pageManagesTitle = location.pathname === '/products' || PAGE_MANAGED_TITLE.test(location.pathname);
    // Persist the chosen theme so a refresh keeps it; first visit follows the OS setting.
    const [mode, setMode] = React.useState(() => {
        try {
            const saved = localStorage.getItem('themeMode');
            if (saved === 'light' || saved === 'dark') return saved;
        } catch { }
        return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    useEffect(() => {
        try {
            localStorage.setItem('themeMode', mode);
        } catch { }
    }, [mode]);
    const theme = useMemo(() => {
        if (!mode) return;
        return getTheme(mode)
    }, [mode]);

    const [view, setView] = useState(() => {
        const savedView = localStorage.getItem('profileView');
        return savedView || "orders";
    });

    // Save to localStorage whenever view changes
    useEffect(() => {
        localStorage.setItem('profileView', view);
    }, [view]);


    return (
        <ThemeProvider theme={theme}>
            <div className="app">
                <ScrollToTop>
                    {!pageManagesTitle && <StaticPageTitle pathname={location.pathname} />}
                    <NavBar mode={mode} setMode={setMode} />
                    <LanguageSelector />
                    <TranslatedComponent />
                    <motion.div
                        // refresh state (Home clicked while on Home) forces a remount
                        key={location.pathname + (location.state?.refresh ?? "")}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        style={{ width: "100%" }}
                    >
                        <Outlet />
                    </motion.div>
                    <Footer />
                </ScrollToTop>
            </div>
        </ThemeProvider>
    );
};

// Define router
export const router = createBrowserRouter([
    {
        path: '/',
        element: <Layout />,

        children: [
            { path: '/', element: <Home /> },
            { path: '/products', element: <Products /> },
            { path: '/products/:documentId', element: <Products /> },
            { path: '/products/essence/:slug', element: <Products /> },
            { path: '/product/:id', element: <Product /> },
            { path: '/about', element: <About /> },
            { path: '/contact', element: <Contact /> },
            { path: `/wishlists/me/:id`, element: <Product /> },
            { path: `/wishlists/me`, element: <Wishlists /> },
            { path: "/payment/success", element: <PayUPaymentResultPage /> },
            { path: "/order-status/:token", element: <OrderStatusPage /> },
            // { path: `/wishlists/:id`, element: <Wishlists /> },
            { path: '/login', element: <Login /> },
            { path: '/register', element: <Register /> },
            {
                path: "/profile",
                element: <ProfileWrapper />   // wrapper passes props
            },
            { path: '/profile/orders', element: <Profile /> },

            { path: '/user-confirmed', element: <ConfirmEmail /> },
            { path: '/checkout', element: <CheckoutPage /> },
            { path: '/addresses', element: <Addresses /> },
            { path: '/policy', element: <Policy /> },
            { path: '/returns', element: <Returns /> },
            { path: '/regulations', element: <Regulations /> },
            { path: '/currencies', element: <CurrencySwitcher /> },
            { path: '/change-password', element: <ConfirmEmail /> },
            { path: '/reset-password', element: <ResetPassword /> },
            { path: '/reviews', element: <Reviews /> },
            { path: '/logos/:id', element: <Logo /> },
            { path: '/featured-descs/:id', element: <FeaturedDesc /> },
            { path: '/carts/me', element: <cartReducer /> },
            { path: '/carts/clear', element: <cartReducer /> },
            { path: '/orders', element: <cartReducer /> },
            { path: '/orders/create-payment', element: <cartReducer /> },
            { path: '/shipping', element: <ShippingInfoPage /> },


        ],
    },
]);
function ProfileWrapper() {
    const [mode, setMode] = React.useState("light");
    return <Profile mode={mode} setMode={setMode} />;
}

